export enum CompactNumberUnit {
  Thousand = 'thousand',
  TenThousand = 'tenThousand',
  HundredMillion = 'hundredMillion',
}

export type CompactNumberResult = {
  value: string;
  unit: CompactNumberUnit | null;
};

/**
 * Compact number formatting using Japanese notation units with i18n support.
 *
 * n < 1,000           → plain number          (e.g. 302)
 * 1,000 <= n < 10,000 → {n/千}千 (1 decimal)  (e.g. 3.6千, 3千)
 * 10,000 <= n < 1e8   → {n/万}万 (1 decimal)  (e.g. 44.5万, 44万)
 * n >= 1e8            → {n/億}億 (1 decimal)  (e.g. 3.4億, 3億)
 *
 * The decimal is omitted when the divided result is a whole number.
 * Truncation (floor) is applied at 1 decimal place to prevent range overflow.
 */
export function formatCompactNumber(value: number): CompactNumberResult {
  if (value >= 100_000_000) {
    return {
      value: truncateToOneDecimal(value, 100_000_000),
      unit: CompactNumberUnit.HundredMillion,
    };
  }
  if (value >= 10_000) {
    return {
      value: truncateToOneDecimal(value, 10_000),
      unit: CompactNumberUnit.TenThousand,
    };
  }
  if (value >= 1_000) {
    return {
      value: truncateToOneDecimal(value, 1_000),
      unit: CompactNumberUnit.Thousand,
    };
  }
  return {
    value: String(value),
    unit: null,
  };
}

/**
 * Divides n by divisor and returns a string with at most one decimal place,
 * omitting the decimal if it would be zero. Uses integer arithmetic to avoid
 * floating-point precision issues.
 */
function truncateToOneDecimal(n: number, divisor: number): string {
  const intPart = Math.floor(n / divisor);
  const remainder = n - intPart * divisor;
  const decimalDigit = Math.floor((remainder * 10) / divisor);
  return decimalDigit === 0 ? String(intPart) : `${intPart}.${decimalDigit}`;
}
